import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import { useFeatureStore } from '@/stores/featureStore'
import { publishBasisBody, type PublishBasis } from '@/hooks/useWorkflowConfig'
import type {
  PackApplyResponse,
  PackPreviewResponse,
  PackSummary,
} from '@/types/workflowConfig'

export function workflowPacksQueryKey() {
  return ['workflows', 'packs'] as const
}

/** Starter-pack catalog (SA-84). `enabled` lets callers defer the fetch
 * until the install UI is actually open. */
export function useWorkflowPacks(enabled = true): UseQueryResult<PackSummary[], Error> {
  return useQuery<PackSummary[], Error>({
    queryKey: workflowPacksQueryKey(),
    queryFn: () => apiClient.get<PackSummary[]>('/api/workflows/packs'),
    enabled,
    staleTime: 30_000,
  })
}

interface PackRequestBody {
  pack: string
  take_pack: string[]
}

/** Dry-run: weave a pack under the current overlay and classify each entry
 * (adds/no_change/conflict) without writing anything — no cache side
 * effects. */
export function usePackPreview(
  workflowId: string,
): UseMutationResult<PackPreviewResponse, Error, PackRequestBody> {
  return useMutation({
    mutationFn: (body: PackRequestBody) =>
      apiClient.post<PackPreviewResponse>(
        `/api/workflows/${workflowId}/config/pack/preview`,
        body,
      ),
  })
}

/** Weaves the pack into the draft and publishes. SA-416: asserts the basis
 * its PREVIEW reported, so what is published is what the dialog showed — a
 * draft saved, a base moved or a version published since is a 409, see
 * `writeConflict`. */
export function usePackApply(
  workflowId: string,
): UseMutationResult<PackApplyResponse, Error, PackRequestBody & { basis: PublishBasis }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ basis, ...body }) =>
      apiClient.post<PackApplyResponse>(`/api/workflows/${workflowId}/config/pack/apply`, {
        ...body,
        ...publishBasisBody(basis),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })
}

/** Moves the deployment's vocabulary pointer (E4, SA-385, spec D22):
 * `PUT /api/workflows/packs/vocabulary { pack | null }`. The catalog's
 * `vocabulary_active` flags and the manifest's record noun both change,
 * so the packs query is invalidated and `/api/features` re-fetched. */
export function useSetVocabularyPack(): UseMutationResult<PackSummary[], Error, string | null> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (pack: string | null) =>
      apiClient.put<PackSummary[]>('/api/workflows/packs/vocabulary', { pack }),
    onSuccess: async (packs) => {
      queryClient.setQueryData(workflowPacksQueryKey(), packs)
      queryClient.invalidateQueries({ queryKey: workflowPacksQueryKey() })
      await useFeatureStore.getState().loadFeatures()
    },
  })
}
